import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Globe2, X } from "lucide-react";

export const LanguageProvider = (() => {
  type LanguageItem = {
    code: string; // Google's language code, e.g. "es"
    name: string; // Google's display name, e.g. "Spanish"
  };

  const LP = {
    ink: "#0A1464",
    blue: "#0180FC",
    body: "#4A5C99",
    chip: "#E8F3FF",
    sheet: "#EAF5FF",
    handle: "#B9D3EA",
    shadow: "rgba(10,20,100,.2)",
  };

  const LANG_CSS = `
.lp, .lp *, .lp *::before, .lp *::after{ box-sizing:border-box; font-family:'Inter',system-ui,sans-serif; }
.lp button{ cursor:pointer; border:none; background:none; font-family:inherit; color:inherit; }
.lp button:focus-visible{ outline:2px solid ${LP.blue}; outline-offset:2px; }

.lp-tab-wrap{
  position:fixed; top:50%; right:0; z-index:9998;
  transform:translateY(-50%);
  touch-action:pan-y;
}
.lp-fab{
  display:flex; align-items:center; gap:6px;
  width:${`116px`}; height:38px; padding:0 12px 0 14px;
  border-radius:12px 0 0 12px;
  background:#fff; box-shadow:-3px 3px 12px -4px ${LP.shadow}, 0 0 0 1px rgba(10,20,100,.06);
  font-size:11px; font-weight:600; color:${LP.ink};
  white-space:nowrap; overflow:hidden;
  cursor:grab;
}
.lp-fab:active{ cursor:grabbing; }
.lp-fab:hover{ transform:translateY(-1px); }
.lp-fab:active{ transform:scale(.97); }

.lp-scrim{ position:fixed; inset:0; z-index:200; background:rgba(10,20,100,.28); backdrop-filter:blur(6px); display:flex; }
.lp-scrim.mobile{ align-items:flex-end; justify-content:center; }
.lp-scrim.desktop{ align-items:center; justify-content:center; padding:20px; }
.lp-sheet{ position:relative; width:100%; overflow:hidden; display:flex; flex-direction:column; background:${LP.sheet}; color:${LP.ink}; }
.lp-sheet.mobile{ border-radius:28px 28px 0 0; max-height:80vh; padding-bottom:env(safe-area-inset-bottom); }
.lp-sheet.desktop{ max-width:400px; border-radius:28px; max-height:80vh; box-shadow:0 24px 60px -12px ${LP.shadow}; }
.lp-body{ flex:1 1 auto; overflow-y:auto; min-height:0; padding:6px 18px 18px; }
.lp-title{ font-size:22px; font-weight:800; margin:0; }
.lp-sub{ font-size:13px; color:${LP.body}; line-height:1.5; margin:4px 0 16px; }
.lp-list{ display:flex; flex-direction:column; gap:8px; }
.lp-row{ display:flex; align-items:center; gap:12px; width:100%; background:#fff; border-radius:20px; border:2px solid transparent;
  padding:11px 14px; text-align:left; transition:border-color .13s, transform .1s; }
.lp-row:active{ transform:scale(0.99); }
.lp-row.on{ border-color:${LP.blue}; }
.lp-row-ic{ width:40px; height:40px; border-radius:50%; background:${LP.chip}; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:background .13s; }
.lp-row.on .lp-row-ic{ background:${LP.blue}; }
.lp-row-title{ font-size:15px; font-weight:600; color:${LP.ink}; }
.lp-row-check{ width:24px; height:24px; border-radius:50%; background:${LP.blue}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.lp-close{ position:absolute; top:12px; right:12px; width:36px; height:36px; border-radius:50%; background:#fff; display:flex; align-items:center; justify-content:center; box-shadow:0 2px 8px -2px ${LP.shadow}; z-index:1; }
.lp-note{ font-size:13px; color:${LP.body}; text-align:center; padding:20px 0; }
@media (prefers-reduced-motion: reduce){ .lp *{ transition:none !important; } }
`;

  const DragHandle: React.FC = () => (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 12 }}>
      <div style={{ width: 40, height: 5, borderRadius: 999, background: LP.handle }} />
    </div>
  );

  function useIsMobile(breakpoint = 700) {
    const [isMobile, setIsMobile] = useState(
      typeof window !== "undefined" ? window.innerWidth < breakpoint : false
    );
    useEffect(() => {
      const onResize = () => setIsMobile(window.innerWidth < breakpoint);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);
    return isMobile;
  }

  const LanguageContext = createContext<{
    currentLang: string;
    setLang: (lang: string) => void;
    translate: (text: string) => Promise<string>;
    languages: LanguageItem[];
  } | undefined>(undefined);

  // Element attributes worth auto-translating in addition to text nodes.
  const TRANSLATABLE_ATTRS = ["placeholder", "title", "aria-label"];

  // Rough country -> language mapping used only for first-time, location-based
  // language detection (falls back to browser language if this doesn't match).
  const COUNTRY_TO_LANG: Record<string, string> = {
    SA: "ar", AE: "ar", EG: "ar", QA: "ar", KW: "ar", MA: "ar",
    CN: "zh", TW: "zh", HK: "zh", SG: "zh",
    CZ: "cs",
    DK: "da",
    NL: "nl",
    GB: "en", US: "en", AU: "en", NZ: "en", IE: "en", CA: "en", ZA: "en",
    EE: "et",
    FI: "fi",
    FR: "fr", BE: "fr",
    DE: "de", AT: "de", CH: "de",
    GR: "el",
    IN: "hi",
    HU: "hu",
    ID: "id",
    IT: "it",
    JP: "ja",
    KR: "ko",
    MY: "ms",
    NO: "no",
    PL: "pl",
    PT: "pt", BR: "pt",
    RU: "ru",
    ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es",
    KE: "sw", TZ: "sw",
    SE: "sv",
    TH: "th",
    TR: "tr",
    UA: "uk",
    VN: "vi",
  };

  const Provider: React.FC<{ apiKey: string; children: ReactNode }> = ({ apiKey, children }) => {
    const isMobile = useIsMobile();
    const [languages, setLanguages] = useState<LanguageItem[]>([]);
    const [loadingLangs, setLoadingLangs] = useState(true);
    const [langError, setLangError] = useState<string | null>(null);

    // Pull the language list straight from Google's Translate API — no hardcoded array.
    useEffect(() => {
      let cancelled = false;
      (async () => {
        if (!apiKey) {
          setLangError("No API key set.");
          setLoadingLangs(false);
          return;
        }
        try {
          const res = await fetch(
            `https://translation.googleapis.com/language/translate/v2/languages?key=${apiKey}&target=en`
          );
          const data = await res.json();
          if (data.error) throw new Error(data.error.message || "Google API error");
          const list: LanguageItem[] = (data?.data?.languages ?? []).map((l: any) => ({
            code: l.language,
            name: l.name,
          }));
          if (!cancelled) setLanguages(list);
        } catch (err) {
          console.error("Failed to load languages:", err);
          if (!cancelled) setLangError("Couldn't load languages.");
        } finally {
          if (!cancelled) setLoadingLangs(false);
        }
      })();
      return () => { cancelled = true; };
    }, [apiKey]);

    const [currentLang, setCurrentLang] = useState<string>(() => {
      const stored = localStorage.getItem("appLang");
      if (stored) return stored;
      const browserLangs = navigator.languages?.length ? navigator.languages : [navigator.language];
      return browserLangs[0]?.split("-")[0].toLowerCase() || "en";
    });

    const [open, setOpen] = useState(false);
    const cache = useRef<Map<string, string>>(new Map()); // "lang::text" -> translated text

    // ── swipeable edge tab: sits mostly off-screen at rest (only a sliver
    // showing), swipe left to pull it fully into view, swipe right (or just
    // letting go without pulling far enough) tucks it back away. ──
    const TAB_WIDTH = 116;
    const TAB_PEEK = 26; // how much stays visible when tucked away
    const TAB_HIDE_OFFSET = TAB_WIDTH - TAB_PEEK;
    const [pulled, setPulled] = useState(false);
    const dragStartX = useRef(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const handleTabPointerDown = (e: React.PointerEvent) => {
      dragStartX.current = e.clientX;
      setIsDragging(true);
      (e.target as Element).setPointerCapture?.(e.pointerId);
    };
    const handleTabPointerMove = (e: React.PointerEvent) => {
      if (!isDragging) return;
      const delta = e.clientX - dragStartX.current; // negative = dragging left (pulling out)
      const base = pulled ? 0 : TAB_HIDE_OFFSET;
      const next = Math.min(TAB_HIDE_OFFSET, Math.max(0, base + delta));
      setDragOffset(next);
    };
    const handleTabPointerUp = () => {
      if (!isDragging) return;
      setIsDragging(false);
      // snap to whichever side it's closer to
      const shouldPull = dragOffset < TAB_HIDE_OFFSET / 2;
      setPulled(shouldPull);
      setDragOffset(0);
    };
    const tabX = isDragging ? dragOffset : (pulled ? 0 : TAB_HIDE_OFFSET);

    const handleTabTap = () => {
      if (!pulled) { setPulled(true); return; } // first tap on the sliver just pulls it out
      setOpen(true); // already pulled out — open the picker
    };

    const setLang = useCallback((lang: string) => {
      setCurrentLang(lang);
      localStorage.setItem("appLang", lang);
    }, []);

    // First visit only: try to detect language from the device's actual
    // location (via geolocation + reverse geocoding) instead of just the
    // browser's language setting. If the user already has a stored
    // preference (manual or previously detected), this is skipped. If
    // location access is denied, unavailable, or times out, we silently
    // keep the browser-language fallback already set above.
    useEffect(() => {
      if (localStorage.getItem("appLang")) return;
      if (!("geolocation" in navigator)) return;

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await res.json();
            const countryCode: string | undefined = data?.countryCode;
            const detected = countryCode ? COUNTRY_TO_LANG[countryCode.toUpperCase()] : undefined;
            if (detected) setLang(detected);
          } catch (err) {
            console.error("Location-based language detection failed:", err);
          }
        },
        (err) => {
          console.warn("Geolocation unavailable, keeping browser-language default:", err.message);
        },
        { timeout: 8000, maximumAge: 1000 * 60 * 60 }
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Translate many strings in ONE request instead of one request per string.
    const translateBatch = useCallback(
      async (texts: string[], targetLang: string): Promise<Record<string, string>> => {
        const result: Record<string, string> = {};
        if (targetLang === "en") {
          texts.forEach((t) => (result[t] = t));
          return result;
        }
        const toFetch: string[] = [];
        texts.forEach((t) => {
          const cached = cache.current.get(`${targetLang}::${t}`);
          if (cached) result[t] = cached;
          else toFetch.push(t);
        });
        if (toFetch.length === 0) return result;
        if (!apiKey) {
          toFetch.forEach((t) => (result[t] = t));
          return result;
        }
        try {
          const res = await fetch(
            `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ q: toFetch, target: targetLang, source: "en", format: "text" }),
            }
          );
          const data = await res.json();
          if (data.error) {
            console.error("Google Translate API error:", data.error);
            toFetch.forEach((t) => (result[t] = t));
            return result;
          }
          const translations: { translatedText: string }[] = data.data.translations ?? [];
          toFetch.forEach((t, i) => {
            const translated = translations[i]?.translatedText ?? t;
            cache.current.set(`${targetLang}::${t}`, translated);
            result[t] = translated;
          });
        } catch (err) {
          console.error("Translate request failed:", err);
          toFetch.forEach((t) => (result[t] = t));
        }
        return result;
      },
      [apiKey]
    );

    // Kept for any component that wants to translate a single string manually.
    const translate = useCallback(
      async (text: string): Promise<string> => {
        if (!text || !text.trim() || currentLang === "en") return text;
        const map = await translateBatch([text], currentLang);
        return map[text] ?? text;
      },
      [translateBatch, currentLang]
    );

    // ---------------------------------------------------------------------
    // Global, automatic DOM translation — this is what makes it "just work"
    // for every page already wrapped by <LanguageProvider>, with no per-page
    // changes needed.
    // ---------------------------------------------------------------------
    const rootRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<MutationObserver | null>(null);
    const originals = useRef<WeakMap<Node, string>>(new WeakMap()); // node -> original English text
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const shouldSkip = (el: Element | null): boolean => {
      while (el) {
        if (el.hasAttribute?.("data-no-translate")) return true;
        if (["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT"].includes(el.tagName)) return true;
        el = el.parentElement;
      }
      return false;
    };

    const collectWork = (root: Node) => {
      const textNodes: Text[] = [];
      const attrEls: { el: Element; attr: string }[] = [];

      const isAcceptableText = (node: Node) => {
        const text = node.nodeValue;
        return !!text && !!text.trim() && !shouldSkip((node as Text).parentElement);
      };

      if (root.nodeType === Node.TEXT_NODE) {
        if (isAcceptableText(root)) textNodes.push(root as Text);
        return { textNodes, attrEls };
      }

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          return isAcceptableText(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
      });
      let n: Node | null;
      while ((n = walker.nextNode())) textNodes.push(n as Text);

      if (root instanceof Element) {
        TRANSLATABLE_ATTRS.forEach((attr) => {
          root.querySelectorAll(`[${attr}]`).forEach((el) => {
            if (!shouldSkip(el) && el.getAttribute(attr)?.trim()) attrEls.push({ el, attr });
          });
          if (root.hasAttribute(attr) && !shouldSkip(root) && root.getAttribute(attr)?.trim()) {
            attrEls.push({ el: root, attr });
          }
        });
      }

      return { textNodes, attrEls };
    };

    const withMutationsPaused = (fn: () => void) => {
      observerRef.current?.disconnect();
      fn();
      if (rootRef.current) {
        observerRef.current?.observe(rootRef.current, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    };

    const runTranslation = useCallback(
      async (root: Node) => {
        if (!root) return;
        const { textNodes, attrEls } = collectWork(root);
        if (textNodes.length === 0 && attrEls.length === 0) return;

        const getOriginal = (key: Node, current: string) => originals.current.get(key) ?? current;

        if (currentLang === "en") {
          withMutationsPaused(() => {
            textNodes.forEach((node) => {
              const orig = originals.current.get(node);
              if (orig !== undefined && node.nodeValue !== orig) node.nodeValue = orig;
            });
            attrEls.forEach(({ el, attr }) => {
              const orig = originals.current.get(el);
              if (orig !== undefined && el.getAttribute(attr) !== orig) el.setAttribute(attr, orig);
            });
          });
          return;
        }

        const sourceSet = new Set<string>();
        textNodes.forEach((node) => sourceSet.add(getOriginal(node, node.nodeValue || "")));
        attrEls.forEach(({ el, attr }) => sourceSet.add(getOriginal(el, el.getAttribute(attr) || "")));

        const map = await translateBatch(Array.from(sourceSet), currentLang);

        withMutationsPaused(() => {
          textNodes.forEach((node) => {
            const orig = getOriginal(node, node.nodeValue || "");
            if (!originals.current.has(node)) originals.current.set(node, orig);
            const translated = map[orig];
            if (translated && node.nodeValue !== translated) node.nodeValue = translated;
          });
          attrEls.forEach(({ el, attr }) => {
            const orig = getOriginal(el, el.getAttribute(attr) || "");
            if (!originals.current.has(el)) originals.current.set(el, orig);
            const translated = map[orig];
            if (translated && el.getAttribute(attr) !== translated) el.setAttribute(attr, translated);
          });
        });
      },
      [currentLang, translateBatch]
    );

    // Re-translate the whole app whenever the selected language changes.
    useEffect(() => {
      if (rootRef.current) runTranslation(rootRef.current);
  }, [currentLang, runTranslation]);

    // Watch for new content — route changes, async data, modals, etc. —
    // and translate it as it appears, without needing a manual call anywhere.
    useEffect(() => {
      const root = rootRef.current;
      if (!root) return;

      const observer = new MutationObserver((mutations) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          mutations.forEach((m) => {
            if (m.type === "characterData") {
              runTranslation(m.target);
            } else {
              m.addedNodes.forEach((node) => runTranslation(node));
            }
          });
        }, 120);
      });

      observerRef.current = observer;
      observer.observe(root, { childList: true, subtree: true, characterData: true });
      return () => observer.disconnect();
    }, [runTranslation]);
    // ---------------------------------------------------------------------

    const current = languages.find((l) => l.code === currentLang);

    useEffect(() => {
      if (!open) return;
      const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }, [open]);

    return (
      <LanguageContext.Provider value={{ currentLang, setLang, translate, languages }}>
        <div ref={rootRef}>{children}</div>

        <div className="lp" data-no-translate>
          <style>{LANG_CSS}</style>

          <div
            className="lp-tab-wrap"
            style={{
              transform: `translateY(-50%) translateX(${tabX}px)`,
              transition: isDragging ? "none" : "transform .22s cubic-bezier(.2,.85,.3,1.3)",
            }}
          >
            <button
              type="button"
              className="lp-fab"
              onClick={handleTabTap}
              onPointerDown={handleTabPointerDown}
              onPointerMove={handleTabPointerMove}
              onPointerUp={handleTabPointerUp}
              onPointerCancel={handleTabPointerUp}
              aria-label="Change language"
            >
              <Globe2 size={12} color={LP.ink} style={{ flexShrink: 0 }} />
              <span>{current?.name ?? "Language"}</span>
            </button>
          </div>

          <AnimatePresence>
            {open && (
              <motion.div
                className={`lp-scrim ${isMobile ? "mobile" : "desktop"}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setOpen(false)}
              >
                <motion.div
                  role="dialog" aria-modal="true" aria-labelledby="lp-title"
                  className={`lp-sheet ${isMobile ? "mobile" : "desktop"}`}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  initial={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.93, y: 16 }}
                  animate={isMobile ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
                  exit={isMobile ? { y: "100%" } : { opacity: 0, scale: 0.94, y: 12 }}
                  transition={{ type: "spring", stiffness: 320, damping: 30 }}
                >
                  {isMobile ? <DragHandle /> : (
                    <button type="button" className="lp-close" onClick={() => setOpen(false)} aria-label="Close">
                      <X size={16} strokeWidth={2.6} color={LP.ink} />
                    </button>
                  )}

                  <div className="lp-body" style={{ paddingTop: isMobile ? 14 : 22 }}>
                    <h2 id="lp-title" className="lp-title">Select language</h2>
                    <p className="lp-sub">Choose the language you'd like to use.</p>

                    {loadingLangs && <p className="lp-note">Loading languages…</p>}
                    {langError && <p className="lp-note">{langError}</p>}

                    {!loadingLangs && !langError && (
                      <div className="lp-list">
                        {languages.map((lang) => {
                          const active = lang.code === currentLang;
                          return (
                            <button
                              key={lang.code}
                              type="button"
                              className={`lp-row${active ? " on" : ""}`}
                              aria-pressed={active}
                              onClick={() => { setLang(lang.code); setOpen(false); }}
                            >
                              <span className="lp-row-ic"><Globe2 size={18} color={active ? "#fff" : LP.ink} /></span>
                              <span style={{ flex: 1 }}>
                                <div className="lp-row-title">{lang.name}</div>
                              </span>
                              {active && (
                                <span className="lp-row-check"><Check size={14} strokeWidth={3} color="#fff" /></span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </LanguageContext.Provider>
    );
  };

  const useLanguage = () => {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
    return ctx;
  };

  return Object.assign(Provider, { useLanguage });
})();










export type LanguageItem = {  code: string;  symbol: string;  name: string;  flag: string;};
 export const LANGUAGES: LanguageItem[] = [ 
   { code: "ar", symbol: "العربية", name: "Arabic", flag: "🇸🇦" },  { code: "zh", symbol: "中文", name: "Chinese", flag: "🇨🇳" },  { code: "cs", symbol: "Čeština", name: "Czech", flag: "🇨🇿" },  { code: "da", symbol: "Dansk", name: "Danish", flag: "🇩🇰" },  { code: "nl", symbol: "Nederlands", name: "Dutch", flag: "🇳🇱" },  { code: "en", symbol: "English", name: "English", flag: "🇬🇧" },  { code: "et", symbol: "Eesti", name: "Estonian", flag: "🇪🇪" },  { code: "fi", symbol: "Suomi", name: "Finnish", flag: "🇫🇮" },  { code: "fr", symbol: "Français", name: "French", flag: "🇫🇷" },  { code: "de", symbol: "Deutsch", name: "German", flag: "🇩🇪" },  { code: "el", symbol: "Ελληνικά", name: "Greek", flag: "🇬🇷" },  { code: "hi", symbol: "हिन्दी", name: "Hindi", flag: "🇮🇳" },  { code: "hu", symbol: "Magyar", name: "Hungarian", flag: "🇭🇺" },  { code: "id", symbol: "Bahasa Indonesia", name: "Indonesian", flag: "🇮🇩" },  { code: "it", symbol: "Italiano", name: "Italian", flag: "🇮🇹" },  { code: "ja", symbol: "日本語", name: "Japanese", flag: "🇯🇵" },  { code: "ko", symbol: "한국어", name: "Korean", flag: "🇰🇷" },  { code: "ms", symbol: "Bahasa Melayu", name: "Malay", flag: "🇲🇾" },  { code: "no", symbol: "Norsk", name: "Norwegian", flag: "🇳🇴" },  { code: "pl", symbol: "Polski", name: "Polish", flag: "🇵🇱" },  { code: "pt", symbol: "Português", name: "Portuguese", flag: "🇵🇹" },  { code: "ru", symbol: "Русский", name: "Russian", flag: "🇷🇺" },  { code: "es", symbol: "Español", name: "Spanish", flag: "🇪🇸" },  { code: "sw", symbol: "Kiswahili", name: "Swahili", flag: "🇰🇪" },  { code: "sv", symbol: "Svenska", name: "Swedish", flag: "🇸🇪" },  { code: "th", symbol: "ไทย", name: "Thai", flag: "🇹🇭" },  { code: "tr", symbol: "Türkçe", name: "Turkish", flag: "🇹🇷" },  { code: "uk", symbol: "Українська", name: "Ukrainian", flag: "🇺🇦" },  { code: "vi", symbol: "Tiếng Việt", name: "Vietnamese", flag: "🇻🇳" },]; 